import { CalendarDays, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { DateRangePickerInput } from '@/shared/components/inputs/DateRangePicker.input';
import { Button } from '@/shared/components/ui/Button';
import { useDateFormatter } from '@/shared/hooks';
import { QuestionShell } from '../QuestionShell';
import { describeStrings } from '../../utils/describe.strings';
import type { DateAnswer } from '../../types/describe.types';

interface DateQuestionProps {
  status: 'active' | 'answered' | 'upcoming';
  isLast: boolean;
  answer: DateAnswer | null;
  onChange: (answer: DateAnswer | null) => void;
}

export const DateQuestion = ({
  status,
  isLast,
  answer,
  onChange,
}: DateQuestionProps) => {
  const { t } = useTranslation('common');
  const { formatDate } = useDateFormatter();

  if (status === 'answered' && answer) {
    const start = new Date(answer.startDate);
    const end = new Date(answer.endDate);
    return (
      <QuestionShell
        kind="core"
        stepNumber={3}
        heading={`3. ${describeStrings.date.heading}`}
        status="answered"
        isLast={isLast}
      >
        <div className="inline-flex items-center gap-2">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-white border border-slate-200 shadow-sm rounded-lg text-sm font-medium text-slate-900">
            <CalendarDays className="h-4 w-4 text-slate-400" aria-hidden />
            {`${formatDate(start, 'medium')} – ${formatDate(end, 'medium')}`}
          </div>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label={t('clear')}
            onClick={() => onChange(null)}
            className="h-8 w-8 text-slate-400 hover:text-slate-600"
          >
            <X className="h-4 w-4" aria-hidden />
          </Button>
        </div>
      </QuestionShell>
    );
  }

  const maxDate = new Date();

  return (
    <QuestionShell
      kind="core"
      stepNumber={3}
      heading={describeStrings.date.heading}
      subheading={describeStrings.date.subheading}
      status={status}
      isLast={isLast}
    >
      <div className="max-w-md">
        <DateRangePickerInput
          value={
            answer
              ? {
                  from: new Date(answer.startDate),
                  to: new Date(answer.endDate),
                }
              : null
          }
          placeholder={describeStrings.date.placeholder}
          maxDate={maxDate}
          onChange={(range) =>
            onChange(
              range
                ? {
                    startDate: range.from.toISOString(),
                    endDate: range.to.toISOString(),
                  }
                : null
            )
          }
        />
      </div>
    </QuestionShell>
  );
};
